import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { testimonials } from '../data/testimonialsData';
import {
  PhoneCall, Phone, Clock, Users, ShieldCheck, ArrowRight,
  Headset, Mail, PhoneOutgoing, Wrench, HeartPulse,
  Truck, Briefcase, Monitor, Landmark, ShoppingBag, Factory, Building2,
  Home as HomeIcon, Scale, GraduationCap, Car, Zap, Shield, HeartHandshake,
} from 'lucide-react';
import { AnimatedCounter } from '../components/AnimatedCounter';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const industries = [
  { icon: Wrench,      title: 'Trades & Field',   desc: 'Capture leads while on-site.',           href: '/industries#trades' },
  { icon: HeartPulse, title: 'Healthcare',         desc: 'Compassionate patient scheduling.',      href: '/industries#healthcare' },
  { icon: Truck,       title: 'Logistics',          desc: 'Urgent dispatch coordination.',          href: '/industries#logistics' },
  { icon: Briefcase,   title: 'Professional',       desc: 'Premium front-of-house presence.',       href: '/industries#professional' },
  { icon: Monitor,     title: 'IT & Technology',    desc: 'First-line support triage.',             href: '/industries#it' },
  { icon: Landmark,    title: 'Finance',            desc: 'Screening urgent queries.',              href: '/industries#finance' },
  { icon: ShoppingBag, title: 'Retail',             desc: 'Handling order queries.',                href: '/industries#retail' },
  { icon: Factory,     title: 'Manufacturing',      desc: 'Supplier and logistics queries.',        href: '/industries#manufacturing' },
  { icon: Building2,    title: 'Facilities & Field',  desc: 'Fault logging and visit coordination.',   href: '/industries#facilities' },
  { icon: HomeIcon,     title: 'Estate Agents',       desc: 'Leads, viewings, tenant emergencies.',    href: '/industries#property' },
  { icon: Scale,        title: 'Legal',               desc: 'Professional client intake.',             href: '/industries#legal' },
  { icon: GraduationCap, title: 'Education',          desc: 'Admissions, absences, enquiries.',        href: '/industries#education' },
  { icon: Car,          title: 'Automotive',          desc: 'Bookings, breakdowns, recovery.',         href: '/industries#automotive' },
  { icon: Zap,          title: 'Energy & Utilities',  desc: '24/7 fault lines and dispatch.',          href: '/industries#energy' },
  { icon: Shield,       title: 'Security',            desc: 'Alarm response and key holder contact.',  href: '/industries#security' },
  { icon: HeartHandshake, title: 'Charities',         desc: 'Compassionate helpline support.',         href: '/industries#charity' },
];


const Home = () => {
  const scrollToHowItWorks = () => {
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="flex flex-col min-h-screen">

      {/* ── Hero ── */}
      <section className="relative pt-28 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-blue-50 via-white to-white" />
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.div variants={fadeIn} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cygnet-dark/10 text-cygnet-dark font-medium text-sm mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cygnet-dark opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-cygnet-dark" />
                </span>
                24/7 UK Call Centre Services
              </motion.div>

              <motion.h1 variants={fadeIn} className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-gray-900 mb-6 leading-[1.1]">
                Every missed call is <span className="text-cygnet-dark">lost revenue.</span>
              </motion.h1>

              <motion.p variants={fadeIn} className="text-xl text-gray-500 mb-4 leading-relaxed">
                We act as an extension of your team, providing professional outsourced call handling so you can focus on growing your business while we ensure you never miss an opportunity.
              </motion.p>

              <motion.p variants={fadeIn} className="text-base font-semibold text-cygnet-dark mb-8 tracking-wide">
                We listen. We care. We deliver. <span className="font-normal text-gray-500">— trusted since 2000.</span>
              </motion.p>

              <motion.div variants={fadeIn} className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="inline-flex items-center justify-center rounded-md bg-cygnet-dark px-8 py-4 text-base font-semibold text-white shadow-sm hover:bg-cygnet-DEFAULT transition-colors">
                  Get in Touch
                </Link>
                <button onClick={scrollToHowItWorks} className="inline-flex items-center justify-center rounded-md border border-cygnet-dark px-8 py-4 text-base font-semibold text-cygnet-dark hover:bg-gray-50 transition-colors">
                  See How It Works
                </button>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block"
            >
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            >
              <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl relative">
                <img
                  src="https://images.pexels.com/photos/8867415/pexels-photo-8867415.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                  alt="Professional call centre team"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-slate-900/80" />
                <div className="relative h-full flex flex-col justify-between p-8">
                  <div className="flex justify-between items-start">
                    <div className="bg-white/20 backdrop-blur-md p-3 rounded-xl border border-white/20">
                      <PhoneCall className="w-8 h-8 text-white" />
                    </div>
                    <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                      Live
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-white/90 backdrop-blur-lg rounded-xl p-4 text-gray-900 shadow-xl">
                      <div className="text-sm text-gray-500 font-medium mb-1">Incoming Call</div>
                      <div className="font-bold text-lg">New Client Inquiry</div>
                      <div className="flex items-center gap-2 mt-3 text-sm font-medium text-cygnet-dark">
                        <Clock className="w-4 h-4" />
                        <span>Answered in 3 seconds</span>
                      </div>
                    </div>
                    <div className="bg-cygnet-dark text-white rounded-xl p-4 shadow-xl translate-x-4">
                      <div className="text-sm text-white/70 font-medium mb-1">Action Taken</div>
                      <div className="font-bold">Message dispatched to team</div>
                      <div className="flex items-center gap-2 mt-3 text-sm font-bold">
                        <ShieldCheck className="w-4 h-4" />
                        <span>Lead Secured</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Trust Bar ── */}
      <section className="border-y border-gray-200 bg-gray-50 py-8">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0">
            {[
              { value: <AnimatedCounter target={26} suffix="+" />, label: 'Years Experience' },
              { value: '100%', label: 'UK-Based Team' },
              { value: '24/7', label: 'Availability' },
              { value: '16+', label: 'Industries Served' },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`text-center px-4 border-b md:border-b-0 pb-6 md:pb-0 ${i < 3 ? 'md:border-r border-gray-200' : ''}`}
              >
                <div className="text-3xl font-bold text-cygnet-dark mb-1">{stat.value}</div>
                <div className="text-sm font-medium text-gray-500">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Problem ── */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">The modern business dilemma</h2>
            <p className="text-lg text-gray-500">Operating a business is demanding enough without having to constantly manage the phones.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: PhoneCall, color: 'bg-red-50 text-red-600', title: 'Missed calls = lost customers', desc: "68% of callers who go to voicemail don't leave a message. They hang up and call your competitor instead." },
              { icon: Users,     color: 'bg-orange-50 text-orange-600', title: 'Hiring staff is expensive', desc: 'Recruiting, training, and managing full-time receptionists costs tens of thousands of pounds annually.' },
              { icon: Clock,     color: 'bg-blue-50 text-cygnet-dark', title: '24/7 expectations', desc: "Customers no longer stick to 9-to-5. They expect immediate responses evenings, weekends, and holidays." },
            ].map((card, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-100px' }}
                variants={fadeIn}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                transition={{ delay: i * 0.2 }}
                className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm"
              >
                <div className={`w-12 h-12 ${card.color} rounded-xl flex items-center justify-center mb-6`}>
                  <card.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{card.title}</h3>
                <p className="text-gray-500 leading-relaxed">{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Solution ── */}
      <section className="py-16 md:py-24 bg-slate-900 text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">We are the solution.</h2>
              <p className="text-lg text-white/70 mb-4 leading-relaxed">
                Cygnet provides premium outsourced call handling that transforms your availability without the overhead of full-time staff. We seamlessly integrate with your operations.
              </p>
              <p className="text-sm font-semibold text-cygnet-light mb-8 uppercase tracking-wider">
                Trusted by UK businesses for over 26 years — established 2000.
              </p>
              <ul className="space-y-6">
                {[
                  { icon: Clock,       title: 'Always On',         desc: 'True 24/7/365 coverage, ensuring you never miss an inquiry.' },
                  { icon: ShieldCheck, title: 'No Hidden Overheads', desc: 'No salary, NI, pension, or recruitment costs. You only pay for the coverage you need.' },
                  { icon: Users,       title: 'Professional Polish', desc: 'Trained UK operators acting as a seamless extension of your brand.' },
                ].map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex gap-4 list-none"
                  >
                    <div className="bg-white/10 p-3 rounded-lg h-fit">
                      <item.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">{item.title}</h4>
                      <p className="text-white/60 mt-1">{item.desc}</p>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="rounded-2xl border border-white/10 p-8 flex flex-col justify-center space-y-4">
                <div className="text-xs font-semibold text-white/40 uppercase tracking-widest mb-2">Hiring in-house vs Cygnet</div>

                {/* In-house column */}
                <div className="bg-white/5 border border-white/15 p-4 rounded-xl">
                  <div className="text-sm font-semibold text-white/70 mb-3">Hiring in-house</div>
                  {['£25k+ salary per year', 'NI + pension on top', 'Recruitment & training costs', 'Business hours only'].map((item) => (
                    <div key={item} className="flex items-center gap-2 text-sm text-red-400 mb-1.5">
                      <span>✕</span>
                      {item}
                    </div>
                  ))}
                </div>

                {/* Cygnet column */}
                <div className="bg-emerald-500/10 border border-emerald-500/20 p-4 rounded-xl">
                  <div className="text-sm font-semibold text-emerald-300 mb-3">Cygnet Call Centre</div>
                  {['No salary, NI, or pension', 'No recruitment or training fees', '24/7 coverage available', 'Scales with your business'].map((item) => (
                    <div key={item} className="flex items-center gap-2 text-sm text-emerald-400 mb-1.5">
                      <span>✓</span>
                      {item}
                    </div>
                  ))}
                </div>
              </div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-cygnet-dark/30 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Services Overview ── */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
            <div className="max-w-2xl">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Comprehensive Support</h2>
              <p className="text-lg text-gray-500">Tailored communication services designed around your specific business needs.</p>
            </div>
            <Link to="/services" className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 whitespace-nowrap transition-colors">
              View All Services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Headset,       title: 'Call Handling',       desc: 'Professional answering service to capture every lead.',                href: '/services#call-handling' },
              { icon: Users,        title: 'Virtual Receptionist', desc: 'Dedicated appointment booking and front-of-house.',                   href: '/services#virtual-receptionist' },
              { icon: Mail,         title: 'Email Monitoring',     desc: 'Inbox management during peak times or out-of-hours.',                 href: '/services#email-monitoring' },
              { icon: PhoneOutgoing,title: 'Duty of Care Calls',   desc: 'Welfare-focused follow-up calls and appointment reminders.',          href: '/services#duty-of-care' },
            ].map((service, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group p-6 rounded-2xl border border-gray-200 bg-white hover:shadow-md hover:border-cygnet-dark/30 transition-all flex flex-col"
              >
                <div className="w-12 h-12 bg-cygnet-dark/10 text-cygnet-dark rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <service.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-500 text-sm mb-6 flex-grow">{service.desc}</p>
                <Link to={service.href} className="text-cygnet-dark font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all mt-auto">
                  Learn more <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className="py-16 md:py-24 bg-gray-50 border-y border-gray-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How it works</h2>
            <p className="text-lg text-gray-500">Getting started is simple. We can be up and running in days, acting seamlessly as your team.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-12 left-[15%] right-[15%] h-0.5 bg-gray-200 -z-10" />
            {[
              { num: '01', title: 'Seamless Integration', desc: 'We learn your business, set up call forwarding, and align with your brand voice.' },
              { num: '02', title: 'Professional Handling', desc: 'Our UK operators answer your calls precisely as if they were sitting in your office.' },
              { num: '03', title: 'Instant Delivery',     desc: 'You receive detailed messages immediately or we live-transfer urgent calls to you.' },
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.2 }}
                className="text-center"
              >
                <div className="w-24 h-24 mx-auto bg-white border-4 border-gray-100 rounded-full flex items-center justify-center text-2xl font-bold text-cygnet-dark mb-6 shadow-sm">
                  {step.num}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-500 leading-relaxed max-w-xs mx-auto">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries Grid ── */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Trusted across sectors</h2>
            <p className="text-lg text-gray-500">We understand the specific language, urgency, and operational needs of various industries.</p>
          </div>

          {/* Mobile: compact pill grid */}
          <div className="grid grid-cols-2 gap-3 md:hidden">
            {industries.map((ind, i) => (
              <Link to={ind.href} key={i}>
                <motion.div
                  whileTap={{ scale: 0.97 }}
                  className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 hover:border-cygnet-dark/40 transition-colors"
                >
                  <div className="w-8 h-8 shrink-0 bg-slate-50 text-cygnet-dark rounded-lg flex items-center justify-center">
                    <ind.icon className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900 leading-tight">{ind.title}</span>
                </motion.div>
              </Link>
            ))}
          </div>

          {/* Desktop: full cards */}
          <div className="hidden md:grid md:grid-cols-4 lg:grid-cols-4 gap-6">
            {industries.map((ind, i) => (
              <Link to={ind.href} key={i}>
                <motion.div
                  whileHover={{ y: -5 }}
                  className="bg-white border border-gray-200 p-6 rounded-2xl text-center hover:border-cygnet-dark/40 transition-colors h-full"
                >
                  <div className="w-14 h-14 mx-auto bg-slate-50 text-cygnet-dark rounded-full flex items-center justify-center mb-4">
                    <ind.icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{ind.title}</h3>
                  <p className="text-sm text-gray-500">{ind.desc}</p>
                </motion.div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-8 md:mt-10">
            <Link to="/industries" className="text-cygnet-dark font-medium hover:underline inline-flex items-center gap-2">
              See all industry case scenarios <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Pricing Teaser ── */}
      <section className="py-24 bg-gray-50 border-y border-gray-200">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Simple, predictable costs</h2>
          <p className="text-xl text-gray-500 mb-8">Transparent pricing tailored to your business. No setup fees. Only pay for the coverage you need.</p>
          <Link to="/pricing" className="inline-flex items-center justify-center rounded-md bg-cygnet-dark px-8 py-4 text-base font-semibold text-white hover:bg-cygnet-DEFAULT transition-colors">
            View Our Pricing <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="py-16 md:py-24 bg-slate-900 text-white overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mb-10 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Trusted by leading businesses.</h2>
            <p className="text-lg text-white/60 max-w-2xl">See what our clients say about working with Cygnet.</p>
          </div>

          {/* Mobile: compact quote list */}
          <div className="space-y-3 md:hidden">
            {testimonials.slice(0, 3).map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-start gap-4 bg-white/5 border border-white/10 rounded-xl px-4 py-4"
              >
                <div className="text-cygnet-light text-3xl font-serif leading-none opacity-50 shrink-0 mt-0.5">"</div>
                <div className="min-w-0">
                  <p className="text-white/80 text-sm leading-relaxed line-clamp-3">"{t.body}"</p>
                  <div className="font-bold text-white text-sm mt-2">{t.company}</div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Desktop: full cards */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 3) * 0.15 }}
                className="bg-white/5 border border-white/10 p-8 rounded-2xl flex flex-col justify-between"
              >
                <div>
                  <div className="text-cygnet-light text-5xl font-serif leading-none mb-4 opacity-50">"</div>
                  <p className="text-white/80 leading-relaxed mb-6">"{t.body}"</p>
                </div>
                <div className="border-t border-white/10 pt-4">
                  <div className="font-bold text-white">{t.company}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-24 bg-cygnet-dark text-white text-center">
        <div className="mx-auto max-w-3xl px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight">
              Your customers deserve<br className="hidden sm:block" /> to be answered.
            </h2>
            <p className="text-xl text-white/70 max-w-xl mx-auto leading-relaxed">
              Every missed call is a missed opportunity. Let Cygnet be the professional first voice your customers hear — day or night.
            </p>

            {/* Trust signals */}
            <div className="flex flex-wrap justify-center gap-6 text-sm text-white/60 pt-2">
              {['Same-day setup', 'No long-term contracts', 'UK-based operators', '24/7/365 coverage'].map((item) => (
                <span key={item} className="flex items-center gap-1.5">
                  <span className="text-emerald-400">✓</span>
                  {item}
                </span>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 pt-2">
              <Link to="/contact" className="inline-flex items-center justify-center rounded-md bg-white text-cygnet-dark px-8 py-4 text-lg font-bold hover:bg-gray-100 transition-colors w-full sm:w-auto">
                Get a Free Consultation
              </Link>
              <a href="tel:+442085385400" className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 text-white px-8 py-4 text-base font-semibold hover:bg-white/10 transition-colors w-full sm:w-auto">
                <Phone className="w-4 h-4" />
                0208 538 5400
              </a>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Home;
